import { useServerFn } from "@tanstack/react-start";
import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { checkIsAdmin, claimAdminIfFirst } from "@/lib/blog.functions";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminLayout,
  head: () => ({ meta: [{ title: "Admin - KCN" }, { name: "robots", content: "noindex,nofollow" }] }),
});

const NAV = [
  { label: "Posts", to: "/admin", props: { activeOptions: { exact: true } } },
  { label: "Transcript → Articles", to: "/admin/transcript", props: {} },
  { label: "Growth Plan", to: "/admin/growth-plan", props: { search: { id: undefined } } },
  { label: "Analytics", to: "/admin/analytics", props: {} },
  { label: "Campaigns", to: "/admin/campaigns", props: {} },
] as const;

function AdminLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const check = useServerFn(checkIsAdmin);
  const claim = useServerFn(claimAdminIfFirst);
  const [state, setState] = useState<"checking" | "ok" | "denied">("checking");

  useEffect(() => {
    (async () => {
      const res = await check({});
      if (res.isAdmin) return setState("ok");
      // try claim bootstrap
      try {
        const c = await claim({});
        if (c.granted) return setState("ok");
      } catch {}
      setState("denied");
    })();
  }, [check, claim]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  if (state === "checking") {
    return <div className="admin-light min-h-screen flex items-center justify-center text-foreground/65">Checking access…</div>;
  }
  if (state === "denied") {
    return (
      <div className="admin-light min-h-screen flex items-center justify-center p-6 text-center">
        <div className="max-w-md">
          <h1 className="headline-md mb-4">Not authorized</h1>
          <p className="text-foreground/75 mb-6">
            Your account doesn't have admin access. Ask an existing admin to grant your account the admin role.
          </p>
          <Button onClick={signOut}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-light min-h-screen bg-background text-foreground">

      <header className="border-b border-border bg-surface">
        <div className="container-edge grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-3 lg:flex lg:h-16 lg:justify-between lg:py-0">
          <Link to="/admin" className="min-w-0 whitespace-nowrap font-display text-lg">KCN Admin</Link>

          <nav className="hidden items-center gap-x-5 text-sm lg:flex">
            {NAV.map((n: (typeof NAV)[number]) => (
              <Link key={n.label} to={n.to} {...(n.props as object)} className="text-foreground/70 hover:text-foreground" activeProps={{ className: "text-primary" }}>{n.label}</Link>
            ))}
          </nav>

          <Button variant="ghost" onClick={signOut} className="hidden shrink-0 text-muted-foreground lg:inline-flex">
            Sign out
          </Button>

          <Button
            variant="ghost"
            className="h-11 w-11 shrink-0 lg:hidden"
            onClick={() => setMenuOpen((v: boolean) => !v)}
            aria-label="Toggle admin menu"
            aria-expanded={menuOpen}
            aria-controls="admin-mobile-nav"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {menuOpen && (
          <div id="admin-mobile-nav" className="border-t border-border lg:hidden">
            <nav className="container-edge flex flex-col gap-1 py-3 text-sm">
              {NAV.map((n: (typeof NAV)[number]) => (
                <Link key={n.label} to={n.to} {...(n.props as object)} onClick={() => setMenuOpen(false)} className="flex min-h-11 items-center text-foreground/70" activeProps={{ className: "text-primary" }}>{n.label}</Link>
              ))}
              <Button variant="ghost" onClick={signOut} className="h-11 justify-start px-0 text-muted-foreground">Sign out</Button>
            </nav>
          </div>
        )}
      </header>
      <main className="container-edge py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
